import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Row,
  Table,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';
import Datetime from 'react-datetime';
import NumberFormat from 'react-number-format';
import { faExclamationCircle, faSearch, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Flex from '../../../common/Flex';
import FalconCardHeader from '../../../common/FalconCardHeader';
import FullPagination from '../../../common/pagination/FullPagination';
import { Message, Or, PreloadState } from '../../../UI/preloading/PreloadState';
import { isIterableArray } from '../../../../helpers/utils';

const IndicatorCondominiumRow = ({ condominium, total, showOptions }) => {
  const history = useHistory();
  const condominiumId = condominium?.condominium_id || condominium?.id;
  const manager = condominium?.manager?.data || condominium?.manager || null;

  const copyToClipboard = async (value) => {
    if (!value || !navigator?.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
    }
  };

  const getManagerName = (manager) => {
    if (!manager) {
      return 'Administrador no registrado';
    }

    const { firstname, lastname } = manager;
    return `${firstname || ''} ${lastname || ''}`.trim() || 'Administrador no registrado';
  };

  return (
    <tr>
      <td className="align-middle">
        <div className="font-weight-semi-bold">{condominium.name}</div>
        <div className="text-500 fs--1">
          {condominium.identification || condominium.acronym || 'Sin identificación registrada'}
        </div>
      </td>
      <td className="align-middle">
        <div className="d-flex flex-column fs--1">
          <div className="d-flex flex-wrap align-items-center">
            <b className="mr-2">
              <NumberFormat
                prefix={`${manager?.dni?.charAt(0) || ''}-`}
                thousandSeparator
                value={manager?.dni?.substring(1, 50) || ''}
                displayType="text"
              />
            </b>
            <span>{getManagerName(manager)}</span>
          </div>
          <div className="d-flex flex-wrap align-items-center text-500">
            <span className="mr-3">{manager?.phone || 'Teléfono no registrado'}</span>
            {manager?.username && (
              <div className="d-flex align-items-center" style={{ minWidth: 0, gap: '0.4rem' }}>
                <span id={`manager-email-${condominiumId}`} className="text-truncate" style={{ maxWidth: '100%' }}>
                  {manager.username}
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-500"
                  id={`manager-email-copy-${condominiumId}`}
                  onClick={() => copyToClipboard(manager.username)}
                  aria-label="Copiar correo del administrador"
                >
                  <FontAwesomeIcon icon="copy" />
                </button>
                <UncontrolledTooltip placement="top" target={`manager-email-${condominiumId}`}>
                  {manager.username}
                </UncontrolledTooltip>
                <UncontrolledTooltip placement="top" target={`manager-email-copy-${condominiumId}`}>
                  Copiar correo
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="align-middle text-center">
        {!condominium.banned ? (
          <Badge color="success" pill>Activo</Badge>
        ) : (
          <Badge color="danger" pill>Desactivado</Badge>
        )}
      </td>
      <td className="align-middle text-center font-weight-bold">
        <NumberFormat value={total} displayType="text" thousandSeparator />
      </td>
      {showOptions && (
        <td className="align-middle text-center">
          <UncontrolledDropdown direction="left">
            <DropdownToggle color="falcon-default" size="sm">
              <FontAwesomeIcon icon="ellipsis-h" />
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem onClick={() => history.push(`/root/indicators/${condominiumId}/payments/properties`)}>
                Ver detalles
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </td>
      )}
    </tr>
  );
};

const IndicatorsTable = ({
  data,
  tableData,
  error,
  isLoading,
  meta,
  nextPage,
  previousPage,
  perPage,
  goToPage,
  recordFilter,
  setRecordFilter,
  textSearch,
  setTextSearch,
  applyTextSearch,
  showDateFilter,
  setShowDateFilter,
  tableDateRange,
  setTableDateRange,
  onTableDateRangeChange,
  applyFilters,
  resetTableDateFromGlobal,
  selectedMetric,
  selectedMetricLabel,
  totalsByCondominium,
  totalsByCondominiumLoading,
  tableRangeSource,
  scopeBannerText,
  tableDateValidationMessage,
}) => {
  const noSelection = !selectedMetric;
  const showOptionsColumn = selectedMetric === 'ownerPayments';
  const loading = isLoading || (!noSelection && totalsByCondominiumLoading);

  return (
    <Card>
      <FalconCardHeader title="Condominios">
        <Row>
          <Col>
            <Row className="mb-2">
              <Col className="pr-0">
                <Flex align="center">
                  <Label className="mb-0 mr-2">Filtrar</Label>
                  <Input
                    value={recordFilter}
                    type="select"
                    onChange={({ target }) => {
                      setRecordFilter(target.value);
                    }}
                  >
                    <option value="all">Todos</option>
                    <option value="with">Con registros</option>
                    <option value="without">Sin registros</option>
                  </Input>
                </Flex>
              </Col>
            </Row>
            <Row>
              <Col className="d-flex">
                <Input
                  type="text"
                  value={textSearch}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    applyTextSearch(event.currentTarget.value);
                  }}
                  onChange={(event) => {
                    const value = event.target.value;
                    setTextSearch(value);
                    if (value.trim() === '') applyTextSearch('');
                  }}
                  placeholder="Nombre, sigla, identificación, estado, ciudad o administrador"
                  className="d-inline-block search-input mr-2"
                />
                <Button
                  className="d-inline-block text-nowrap"
                  color="falcon-default"
                  onClick={() => applyTextSearch()}
                >
                  <FontAwesomeIcon icon={faSearch} className="mr-2" />
                  Buscar
                </Button>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col className="d-flex align-items-center flex-wrap">
                <Label className="mb-0 text-600">Filtrar por fecha</Label>
                <Input
                  id="indicator-date-filter"
                  type="checkbox"
                  style={{ cursor: 'pointer' }}
                  className="mt-0 position-static ml-2"
                  checked={showDateFilter}
                  onChange={({ target }) => {
                    setShowDateFilter(target.checked);
                    if (!target.checked) {
                      setTableDateRange({ since: null, to: null });
                      applyFilters({ since: null, to: null });
                    }
                  }}
                />
              </Col>
            </Row>
            {showDateFilter && (
              <Row className="justify-content-center justify-content-md-start align-items-center mt-2">
                <Col md={4} sm={12} className="my-2 my-md-0">
                  <Datetime
                    value={tableDateRange.since}
                    isValidDate={(current) => !current.isAfter()}
                    onChange={(d) => onTableDateRangeChange('since', d)}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    inputProps={{ placeholder: 'DD/MM/AAAA' }}
                  />
                </Col>
                <Col xs="auto">
                  <b>a</b>
                </Col>
                <Col md={4} sm={12} className="my-2 my-md-0">
                  <Datetime
                    value={tableDateRange.to}
                    isValidDate={(current) => (!tableDateRange.since || current.isAfter(tableDateRange.since)) && !current.isAfter()}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    inputProps={{ placeholder: 'DD/MM/AAAA' }}
                    onChange={(d) => onTableDateRangeChange('to', d)}
                  />
                </Col>
                <Col xs="auto" className="d-flex align-items-center">
                  <Button
                    className="text-nowrap"
                    color="falcon-default"
                    disabled={!tableDateRange.since && !tableDateRange.to}
                    onClick={() => applyFilters(tableDateRange)}
                  >
                    <FontAwesomeIcon icon={faSearch} className="mr-2" />
                    Aplicar rango
                  </Button>
                  <Button
                    id="reset-table-sync"
                    className="ml-2"
                    color="falcon-default"
                    size="sm"
                    onClick={resetTableDateFromGlobal}
                    aria-label="Restablecer sincronización"
                  >
                    <FontAwesomeIcon icon={faUndo} />
                  </Button>
                  <UncontrolledTooltip placement="top" target="reset-table-sync">
                    Restablecer sincronización con el periodo superior
                  </UncontrolledTooltip>
                </Col>
              </Row>
            )}
            {!!tableDateValidationMessage && (
              <Row className="mt-2">
                <Col>
                  <Alert color="warning" className="mb-0 py-2">
                    {tableDateValidationMessage}
                  </Alert>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </FalconCardHeader>
      <CardBody className={classNames('fs--1', { 'p-0': isIterableArray(data) })}>
        {!noSelection && (
          <Alert color="light" className="m-3 mb-0 border">
            {scopeBannerText} {tableRangeSource === 'inherited' ? '(Periodo superior)' : '(Periodo personalizado)'}
          </Alert>
        )}
        {noSelection && (
          <Alert color="info" className="m-3 mb-0">
            Para visualizar la data de condominios, primero selecciona uno de los indicadores de arriba.
          </Alert>
        )}
        <PreloadState cond={isIterableArray(tableData)} loading={loading}>
          <Or value={true}>
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <th className="align-middle">Condominio</th>
                  <th className="align-middle">Datos del administrador</th>
                  <th className="align-middle text-center">Estado</th>
                  <th className="align-middle text-center">Total</th>
                  {showOptionsColumn && <th className="align-middle text-center">Opciones</th>}
                </tr>
              </thead>
              <tbody>
                {tableData.map((condominium) => (
                  <IndicatorCondominiumRow
                    key={condominium.condominium_id || condominium.id}
                    condominium={condominium}
                    showOptions={showOptionsColumn}
                    total={
                      Number(condominium?.totalCounts)
                      || totalsByCondominium[condominium.condominium_id || condominium.id]
                      || 0
                    }
                  />
                ))}
              </tbody>
            </Table>
          </Or>
          <Or cond={!!error} value={false}>
            <Or value={false}>
              <Message className="py-3" color="info" icon={faExclamationCircle}>
                {noSelection
                  ? 'Selecciona un indicador para mostrar resultados.'
                  : `No hay condominios para mostrar en ${selectedMetricLabel}.`}
              </Message>
            </Or>
            <Or value={true}>
              <Message className="py-3" color="danger" icon={faExclamationCircle}>
                Ha ocurrido un error.
              </Message>
            </Or>
          </Or>
        </PreloadState>
      </CardBody>
      {isIterableArray(tableData) && (
        <CardFooter className="border-top">
          <FullPagination
            nextPage={nextPage}
            prevPage={previousPage}
            goToPage={goToPage}
            meta={meta}
            className={'shadow-none'}
            posText={'condominios'}
            perPage={perPage}
          />
        </CardFooter>
      )}
    </Card>
  );
};

export default IndicatorsTable;